import React from 'react';
import { CheckCircle, ShieldCheck, ChevronRight, Sparkles, Calendar, Clock, CreditCard, Crown } from 'lucide-react';

const ConfirmationScreen = ({
  formData,
  selectedSpecialty,
  selectedDate,
  selectedTime,
  specialtyConfig,
  selectedSlot,
  paymentResult,
  onCompleteMedicalRecord,
  onStartVerification,
  onReset
}) => {
  const specialtyName = specialtyConfig[selectedSpecialty]?.name || 'Consulta';
  const isIncluded = paymentResult?.type === 'included';
  // Slot bajo confirmación: la cita quedó como solicitud pendiente de que la
  // doctora la acepte, no como cita agendada en firme.
  const isOnDemand = paymentResult?.isOnDemand === true;

  // ── Doctor name resolution (multiple fallbacks) ──
  const prof = paymentResult?.consultationData?.result?.data?.consultation?.professional;
  const slotDoctor = selectedSlot?.professional_ids?.[0];
  const doctorName = prof
    ? `Dra. ${prof.name}${prof.last_name ? ' ' + prof.last_name : ''}`
    : slotDoctor?.name
      ? `Dra. ${slotDoctor.name}`
      : null;
  const doctorAvatar = prof?.avatar || null;
  const doctorLicense = prof?.professional_license || null;

  // ── Date formatting ──
  const formatDate = (date) => {
    if (!date) return '';
    return date.toLocaleDateString('es-MX', {
      weekday: 'long',
      day: 'numeric',
      month: 'long'
    });
  };

  return (
    <div className="min-h-screen bg-warm-canvas flex flex-col animate-fadeIn">
      {/* ─── Top success area ─── */}
      <div className="bg-daisies pt-12 pb-16 px-6 text-center relative overflow-hidden">
        <div className="relative z-10">
          {/* Success checkmark */}
          <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="text-mint-deep" size={36} strokeWidth={2.5} />
          </div>

          <h1 className="text-xl font-bold text-white mb-1 tracking-tight">
            {isOnDemand ? '¡Solicitud enviada!' : '¡Consulta agendada!'}
          </h1>
          <p className="text-white/80 text-sm">
            {isOnDemand
              ? `${doctorName || 'La doctora'} tiene 24 h para confirmar tu cita`
              : 'Revisa los detalles de tu cita'}
          </p>
        </div>
      </div>

      {/* ─── Main content card (overlaps the purple area) ─── */}
      <br />
      <br />
      <div className="flex-1 px-4 -mt-8">
        <div className="max-w-lg mx-auto space-y-3">

          {/* Appointment details card */}
          <div className="bg-white rounded-card shadow-elevated p-5">
            {/* Doctor info */}
            {doctorName && (
              <div className="flex items-center gap-3 mb-4 pb-4 border-b border-brand-100">
                {doctorAvatar ? (
                  <img
                    src={doctorAvatar}
                    alt={doctorName}
                    className="w-12 h-12 rounded-[14px] object-cover"
                    onError={(e) => { e.target.onerror = null; e.target.style.display = 'none'; }}
                  />
                ) : (
                  <div className="w-12 h-12 bg-daisies/10 rounded-[14px] flex items-center justify-center">
                    <span className="text-daisies font-bold text-sm">
                      {specialtyName?.substring(0, 2)}
                    </span>
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-text-primary truncate">{doctorName}</p>
                  <p className="text-text-secondary text-sm">{specialtyName}</p>
                  {doctorLicense && (
                    <p className="text-text-secondary text-[11px] mt-0.5">Cédula: {doctorLicense}</p>
                  )}
                </div>
              </div>
            )}

            {/* Date, time, specialty grid */}
            <div className="grid grid-cols-2 gap-x-4 gap-y-3">
              <div>
                <p className="text-[11px] font-medium text-text-secondary uppercase tracking-wider mb-0.5">Fecha</p>
                <div className="flex items-center gap-1.5">
                  <Calendar size={13} className="text-daisies" />
                  <p className="text-sm font-semibold text-text-primary capitalize">
                    {formatDate(selectedDate)}
                  </p>
                </div>
              </div>
              <div>
                <p className="text-[11px] font-medium text-text-secondary uppercase tracking-wider mb-0.5">Hora</p>
                <div className="flex items-center gap-1.5">
                  <Clock size={13} className="text-daisies" />
                  <p className="text-sm font-semibold text-text-primary">{selectedTime}</p>
                </div>
              </div>

              {!doctorName && (
                <div className="col-span-2">
                  <p className="text-[11px] font-medium text-text-secondary uppercase tracking-wider mb-0.5">Especialidad</p>
                  <p className="text-sm font-semibold text-text-primary">{specialtyName}</p>
                </div>
              )}
            </div>
          </div>

          {/* Payment summary card — only for paid consultations */}
          {!isIncluded && (
            <div className="bg-white rounded-card p-5">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-surface rounded-[12px] flex items-center justify-center">
                    <CreditCard size={18} className="text-daisies" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-text-primary">
                      {paymentResult?.cardLast4
                        ? `•••• ${paymentResult.cardLast4}`
                        : 'Pago procesado'}
                    </p>
                    {paymentResult?.transactionId && (
                      <p className="text-[11px] text-text-secondary font-mono">
                        ID: {paymentResult.transactionId}
                      </p>
                    )}
                  </div>
                </div>
                <div className="text-right">
                  {paymentResult?.appliedCoupon && paymentResult?.originalAmount && (
                    <p className="text-xs text-text-secondary line-through">
                      ${paymentResult.originalAmount.toFixed(2)}
                    </p>
                  )}
                  <p className="text-lg font-bold text-daisies">
                    ${paymentResult?.amount?.toFixed(2) || '450.00'}
                  </p>
                </div>
              </div>

              {paymentResult?.appliedCoupon && (
                <div className="mt-3 pt-3 border-t border-brand-100 flex items-center gap-2">
                  <span className="text-[11px] bg-mint-pill text-mint-deep font-semibold px-2 py-0.5 rounded-full">
                    {paymentResult.appliedCoupon.code}
                  </span>
                  <span className="text-xs text-mint-deep">
                    {paymentResult.appliedCoupon.discountType === 'amount'
                      ? `-$${paymentResult.appliedCoupon.discountAmount.toFixed(2)}`
                      : `-${paymentResult.appliedCoupon.discountPercentage}%`}
                  </span>
                </div>
              )}
            </div>
          )}

          {/* Included consultation badge — only for membership consultations */}
          {isIncluded && (
            <div className="bg-white rounded-card p-5">
              <div className="p-3 rounded-input bg-daisies-50 flex items-center gap-2">
                <Crown size={18} className="text-daisies flex-shrink-0" />
                <p className="text-sm text-daisies font-medium">Incluida con tu membresía, sin costo adicional</p>
              </div>
            </div>
          )}

          {/* Cancellation policy */}
          <div className="bg-surface rounded-card px-4 py-3">
            <p className="text-[12px] text-text-secondary text-center leading-relaxed">
              Cancela o reagenda hasta 24 hrs antes de tu cita para obtener reembolso.
            </p>
          </div>

          {/* ─── Action buttons ─── */}
          <div className="space-y-2.5 pt-2">
            {/* Only show "Completar expediente" for paid consultations (not included) */}
            {!isIncluded && (
              <button
                onClick={onCompleteMedicalRecord}
                className="w-full bg-daisies text-white py-3.5 rounded-card font-bold text-[15px] hover:bg-daisies-hover active:scale-[0.98] transition-all flex items-center justify-center gap-2"
              >
                <Sparkles size={18} />
                Completar expediente médico
              </button>
            )}

            {onStartVerification && (
              <button
                onClick={onStartVerification}
                className="w-full bg-surface text-text-primary py-3.5 rounded-card font-semibold text-[15px] hover:bg-brand-tint active:scale-[0.98] transition-all flex items-center justify-center gap-2"
              >
                <ShieldCheck size={18} className="text-daisies" />
                Verificar mi cuenta
                <ChevronRight size={16} className="text-text-secondary" />
              </button>
            )}

            <button
              onClick={onReset}
              className="w-full text-text-secondary py-3 rounded-card font-medium text-sm hover:text-text-primary active:scale-[0.98] transition-all"
            >
              Volver al inicio
            </button>
          </div>

          {/* Footer */}
          <div className="text-center pb-6 pt-2">
            <p className="text-[11px] text-text-secondary">
              Te enviamos un correo con los detalles ·{' '}
              <a href="mailto:soporte@daisies.mx" className="text-daisies hover:underline">
                soporte@daisies.mx
              </a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConfirmationScreen;